import { EFinancingCompany } from '@if/core/types'
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material'
import { useTestProfiles } from '@src/data/api/user-api/user-api'
import { reportErrorToConsole } from '@src/services/error-logger'
import { loadTestProfile } from '@src/services/impersonation'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

interface Props {
  open: boolean
  onClose: () => void
}

const COMPANIES = [EFinancingCompany.Iceberg, EFinancingCompany.IFinance]

const ListTestProfilesDialog = ({ open, onClose }: Props) => {
  const { t } = useTranslation()
  const [profiles, isLoading, loadingError] = useTestProfiles(open)
  const [selectedUsername, setSelectedUsername] = useState<string | null>(null)
  const [selectedCompanies, setSelectedCompanies] = useState<EFinancingCompany[]>([EFinancingCompany.Iceberg])
  const [isLoadingProfile, setIsLoadingProfile] = useState(false)

  const toggleCompany = (company: EFinancingCompany, checked: boolean) => {
    setSelectedCompanies((current) =>
      checked ? [...current, company] : current.filter((selected) => selected !== company),
    )
  }

  const handleLoad = () => {
    if (!selectedUsername) return
    setIsLoadingProfile(true)
    loadTestProfile(selectedUsername, selectedCompanies).catch((error) => {
      setIsLoadingProfile(false)
      reportErrorToConsole(error)
    })
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm">
      <DialogTitle>{t('impersonation.title')}</DialogTitle>
      <DialogContent>
        {isLoading && <CircularProgress />}
        {!isLoading && loadingError && <Typography color="error">{loadingError.message}</Typography>}
        {!isLoading && !loadingError && (
          <List>
            {profiles.map((profile) => (
              <Box key={profile.username}>
                <ListItemButton
                  selected={profile.username === selectedUsername}
                  onClick={() => setSelectedUsername(profile.username)}
                >
                  <ListItemText primary={profile.username} secondary={profile.roles.join(', ')} />
                </ListItemButton>
                <Divider />
              </Box>
            ))}
          </List>
        )}
        <Typography sx={{ mt: 2 }}>{t('impersonation.associatedCompanies')}</Typography>
        {COMPANIES.map((company) => (
          <FormControlLabel
            key={company}
            label={company}
            control={
              <Checkbox
                checked={selectedCompanies.includes(company)}
                onChange={(event) => toggleCompany(company, event.target.checked)}
              />
            }
          />
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!selectedUsername || selectedCompanies.length === 0 || isLoadingProfile}
          onClick={handleLoad}
        >
          {t('impersonation.load')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ListTestProfilesDialog
